import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import TableContainer from './components/TableContainer';
import Pagination10 from './components/Pagination10';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import usePagination10 from './components/hooks/usePagination10';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import FornecedorModal from './components/modals/FornecedorModal';
import DataTable from './components/DataTable';
import { formatCPF } from './utils/cpfUtils';
import { formatCNPJ } from './utils/cnpjUtils';

function Fornecedores() {
  const [fornecedores, setFornecedores] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState(false);
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const token = localStorage.getItem('token');
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const vinculo = JSON.parse(localStorage.getItem('vinculo'));
  const isAdmin = vinculo?.tipo === 'ADMIN';
  const importJsonRef = useRef(null);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    if (!vinculo || !vinculo.idEntidade) {
      showDefaultModal('error', 'Selecione uma entidade antes de acessar esta página.');
      window.location.href = '/home';
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API_BASE_URL.FORNECEDOR}/entidade/${vinculo.idEntidade}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setFornecedores(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter fornecedores:', error);
        showDefaultModal('error', 'Erro ao carregar fornecedores.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar]);

  function handleDelete(fornecedor) {
    showConfirmModal('delete', () => handleConfirmDelete(fornecedor));
  }

  function handleConfirmDelete(fornecedor) {
    axios
      .delete(`${API_BASE_URL.FORNECEDOR}/${fornecedor.idEntidade}/${fornecedor.cpfOrCnpj}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        showDefaultModal('success', 'Fornecedor excluído com sucesso!');
        setAtualizar(prev => !prev);
      })
      .catch(error => {
        console.error('Erro ao excluir fornecedor:', error);
        showDefaultModal('error', 'Erro ao excluir o fornecedor!');
      });
  }

  function handleEdit(fornecedor) {
    setFornecedorSelecionado(fornecedor);
    setShowModal(true);
  }

  function handleNew() {
    setFornecedorSelecionado(null);
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
    setFornecedorSelecionado(null);
  }

  function handleSaveSuccess() {
    setAtualizar(prev => !prev);
    setFornecedorSelecionado(null);
  }

  function handleImportJson(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async ev => {
      let data;
      try { data = JSON.parse(ev.target.result); } catch { showDefaultModal('error', 'Arquivo JSON inválido.'); return; }
      const list = Array.isArray(data) ? data : [data];
      setLoading(true);
      try {
        const result = await axios.post(
          `${API_BASE_URL.FORNECEDOR}/importar?idEntidade=${encodeURIComponent(vinculo.idEntidade)}`,
          list,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const { saved, errors } = result.data;
        if (errors > 0) {
          showDefaultModal('error', `Importação concluída com erros: ${saved} salvo(s), ${errors} erro(s).`);
        } else {
          showDefaultModal('success', `${saved} fornecedor(es) importado(s) com sucesso.`);
        }
        setAtualizar(prev => !prev);
      } catch {
        showDefaultModal('error', 'Erro ao importar fornecedores.');
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
  }

  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const fornecedoresFiltrados = Array.isArray(fornecedores)
    ? fornecedores.filter(f => {
        const normalizedBusca = normalizeString(filtro);
        return (
          normalizeString(f.nome || '').includes(normalizedBusca) ||
          normalizeString(f.cpfOrCnpj || '').includes(normalizedBusca) ||
          normalizeString(f.email || '').includes(normalizedBusca) ||
          normalizeString(f.logrCidade || '').includes(normalizedBusca)
        );
      })
    : [];

  const totalItems = fornecedoresFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const paginaAtual = setPagination.currentPage;
  const itensPorPagina = setPagination.rowsPerPage;

  useEffect(() => {
    setPagination.resetPage();
  }, [filtro]);

  const currentItems = fornecedoresFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Fornecedores" icon="bi-shop" />
        <PageContent>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between gap-2 mb-2 align-items-start align-items-lg-center">
            <div className="botao-grid-2col">
              <button
                className="btn btn-success btn-sm botao-igual"
                onClick={handleNew}
                title="Cadastrar novo fornecedor"
              >
                <i className="bi bi-plus-circle me-1"></i>
                Novo
              </button>
              <button
                className="btn btn-primary btn-sm botao-igual"
                disabled={!fornecedorSelecionado}
                onClick={() => handleEdit(fornecedorSelecionado)}
                title="Editar fornecedor selecionado"
              >
                <i className="bi bi-pencil me-1"></i>
                Editar
              </button>
              <button
                className="btn btn-danger btn-sm botao-igual"
                disabled={!fornecedorSelecionado}
                onClick={() => handleDelete(fornecedorSelecionado)}
                title="Excluir fornecedor selecionado"
              >
                <i className="bi bi-trash me-1"></i>
                Excluir
              </button>
              {isAdmin && (
                <>
                  <button
                    className="btn btn-info btn-sm botao-igual"
                    onClick={() => importJsonRef.current.click()}
                    title="Importar fornecedores de arquivo JSON"
                  >
                    <i className="bi bi-file-arrow-down me-1"></i>
                    Importar JSON
                  </button>
                  <input
                    ref={importJsonRef}
                    type="file"
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={handleImportJson}
                  />
                </>
              )}
            </div>
            <div className="mt-2 mt-lg-0">
              <div className="input-group input-group-sm">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar por nome, CPF/CNPJ, e-mail ou cidade..."
                  value={filtro}
                  onChange={e => setFiltro(e.target.value.toUpperCase())}
                />
              </div>
            </div>
          </div>

          <TableContainer isLoading={isLoading}>
              <DataTable style={{ fontSize: '0.8rem' }} head={<tr>
                  <th scope="col" className="px-2">#</th>
                  <th scope="col" className="px-2">Nome</th>
                  <th scope="col" className="px-2">CPF/CNPJ</th>
                  <th scope="col" className="px-2 d-none d-md-table-cell">Tipo</th>
                  <th scope="col" className="px-2 d-none d-lg-table-cell">E-mail</th>
                  <th scope="col" className="px-2 d-none d-lg-table-cell">Telefone</th>
                  <th scope="col" className="px-2 d-none d-xl-table-cell">Cidade</th>
                  <th scope="col" className="px-2 d-none d-xl-table-cell">UF</th>
                </tr>}>
              <tbody>
                {currentItems.map((fornecedor, idx) => (
                    <tr
                      key={fornecedor.cpfOrCnpj}
                      className={
                        fornecedorSelecionado &&
                        fornecedorSelecionado.cpfOrCnpj === fornecedor.cpfOrCnpj
                          ? 'table-primary'
                          : ''
                      }
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (
                          fornecedorSelecionado &&
                          fornecedorSelecionado.cpfOrCnpj === fornecedor.cpfOrCnpj
                        ) {
                          setFornecedorSelecionado(null);
                        } else {
                          setFornecedorSelecionado(fornecedor);
                        }
                      }}
                    >
                      <td className="px-2">{(paginaAtual - 1) * itensPorPagina + idx + 1}</td>
                      <td className="px-2 text-truncate" style={{ maxWidth: '250px' }}>{fornecedor.nome}</td>
                      <td className="px-2">
                        {fornecedor.tipo === 'PF' ? formatCPF(fornecedor.cpfOrCnpj) :
                         fornecedor.tipo === 'PJ' ? formatCNPJ(fornecedor.cpfOrCnpj) :
                         fornecedor.cpfOrCnpj}
                      </td>
                      <td className="px-2 d-none d-md-table-cell">
                        {fornecedor.tipo === 'PF' ? 'Física' : fornecedor.tipo === 'PJ' ? 'Jurídica' : '-'}
                      </td>
                      <td className="px-2 d-none d-lg-table-cell">
                        {fornecedor.email || '-'}
                      </td>
                      <td className="px-2 d-none d-lg-table-cell">
                        {fornecedor.telefone || fornecedor.celular || '-'}
                      </td>
                      <td className="px-2 d-none d-xl-table-cell">
                        {fornecedor.logrCidade || '-'}
                      </td>
                      <td className="px-2 d-none d-xl-table-cell">
                        {fornecedor.logrUf || '-'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </DataTable>
            </TableContainer>
        </PageContent>
        <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid rgba(0,0,0,0.1)', backgroundColor: '#f8f9fa' }}>
          <Pagination10 {...setPagination} />
        </div>
        <ConfirmModal {...setConfirmModal} />
        <DefaultModal {...setDefaultModal} />
      </ResponsiveShadow>

      {showModal && (
        <FornecedorModal
          show={showModal}
          fornecedor={fornecedorSelecionado}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
        />
      )}
    </Layout>
  );
}

export default Fornecedores;
